import styled from 'styled-components';
import {getColorFromTheme} from '../../../../../../ui/ui-utils';

export const Page = styled.section`
  width: 100%;
  max-width: 1120px;
  margin: 0;
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;

  > button {
    padding: 13px 24px;
  }

  @media (max-width: 640px) {
    flex-direction: column;

    > button {
      width: 100%;
    }
  }
`;

export const PageHeading = styled.div`
  min-width: 0;
`;

export const PageTitle = styled.h1`
  margin: 0;
  color: ${getColorFromTheme('blackTwo')};
  font: 600 24px/32px Graphik, sans-serif;
`;

export const PageSubtitle = styled.p`
  margin: 4px 0 0;
  color: ${getColorFromTheme('textBody')};
  font: 400 16px/24px Graphik, sans-serif;
`;

export const Filters = styled.div`
  display: flex;
  width: fit-content;
  max-width: 100%;
  gap: 4px;
  margin-top: 24px;
  padding: 6px;
  overflow-x: auto;
  border-radius: 10px;
  background: ${getColorFromTheme('backgroundDark')};
`;

export const FilterButton = styled.button<{$active: boolean}>`
  flex: 0 0 auto;
  min-height: 40px;
  padding: 9px 16px;
  border-radius: 8px;
  background: ${({$active}) =>
    $active ? getColorFromTheme('white') : 'transparent'};
  box-shadow: ${({$active, theme}) =>
    $active ? theme.elevation.level2 : 'none'};
  color: ${({$active}) =>
    $active ? getColorFromTheme('purple300') : getColorFromTheme('textBody')};
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  white-space: nowrap;
  transition: color 160ms ease;

  @media (hover: hover) {
    &:hover {
      color: ${getColorFromTheme('purple300')};
    }
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }
`;

export const GroupGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  margin-top: 24px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const GroupCard = styled.article`
  min-width: 0;
  padding: 24px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level1};
`;

export const CardHeader = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const GroupMeta = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
`;

export const GroupTitle = styled.h2`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: ${getColorFromTheme('blackTwo')};
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    flex: 0 0 auto;
  }
`;

export const TypePill = styled.span`
  flex: 0 0 auto;
  max-width: 120px;
  padding: 4px 8px;
  overflow: hidden;
  border-radius: 8px;
  background: ${getColorFromTheme('backgroundDark')};
  color: ${getColorFromTheme('textBody')};
  font-size: 12px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CardActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
`;

export const MemberCount = styled.p`
  margin: 16px 0 20px;
  color: ${getColorFromTheme('textBody')};
  font-size: 14px;
  line-height: 22px;
`;

export const GroupLead = styled.div`
  display: grid;
  min-height: 48px;
  align-content: start;
  gap: 6px;
`;

export const LeadLabel = styled.span`
  color: ${getColorFromTheme('textBody')};
  font-size: 12px;
  line-height: 18px;
`;

export const LeadDetails = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
`;

export const LeadName = styled.span<{$muted?: boolean}>`
  overflow: hidden;
  color: ${({$muted}) =>
    $muted ? getColorFromTheme('textMuted') : getColorFromTheme('textDark')};
  font-size: 14px;
  font-weight: ${({$muted}) => ($muted ? 400 : 500)};
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CardDivider = styled.div`
  height: 1px;
  margin: 20px 0;
  background: ${getColorFromTheme('borderLight')};
`;

export const CardFooter = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const MembersSummary = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  color: ${getColorFromTheme('textBody')};
  font-size: 12px;
  line-height: 18px;
`;

export const MemberAvatars = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-end;
`;

export const MemberAvatar = styled.span`
  display: inline-flex;

  &:not(:last-child) {
    margin-left: -8px;
  }
`;

export const ViewGroupButton = styled.button`
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 6px;
  color: ${getColorFromTheme('purple300')};
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  white-space: nowrap;

  &:hover {
    background: ${getColorFromTheme('backgroundDark')};
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }
`;

export const Loader = styled.div`
  display: flex;
  min-height: 64px;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
`;

export const EmptyState = styled.div`
  display: flex;
  min-height: 220px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 4px;
  margin-top: 24px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('textBody')};
  font-size: 14px;
  line-height: 22px;

  strong {
    color: ${getColorFromTheme('blackTwo')};
    font-size: 16px;
  }
`;

export const Pagination = styled.nav`
  display: flex;
  width: fit-content;
  max-width: 100%;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 32px auto 0;
  padding: 6px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
`;

export const PaginationButton = styled.button<{$active?: boolean}>`
  display: inline-flex;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: ${({$active}) =>
    $active ? getColorFromTheme('backgroundDark') : 'transparent'};
  box-shadow: ${({$active, theme}) =>
    $active ? theme.elevation.level2 : 'none'};
  color: ${({$active}) =>
    $active ? getColorFromTheme('purple300') : getColorFromTheme('textBody')};
  font-size: 14px;
  font-weight: ${({$active}) => ($active ? 600 : 500)};
  line-height: 22px;
  stroke: ${getColorFromTheme('textBody')};

  &:hover:not(:disabled) {
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('purple300')};
    stroke: ${getColorFromTheme('purple300')};
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

export const PaginationStatus = styled.span`
  padding: 0 8px;
  color: ${getColorFromTheme('textBody')};
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;

  @media (max-width: 440px) {
    display: none;
  }
`;
